import { createContext, useContext, useEffect, useState } from 'react';
import { client } from '@/api/client';
import { getTenantSlugFromHostname } from '@/lib/tenantHost';

// Independent of AuthContext on purpose. This lookup has zero dependency on
// auth state -- a logged-out visitor on a tenant subdomain (e.g.
// gftrucklines.prohaul.ai) needs to see that carrier's branding immediately,
// before (and regardless of whether) a session exists. Keeping it in its
// own provider means its mount effect fires on the same initial commit as
// AuthProvider's, in parallel, rather than being sequenced behind the
// session check if it were bolted onto AuthContext instead.
const TenantBrandingContext = createContext();

const INITIAL_STATE = {
  status: 'none', // 'none' | 'loading' | 'found' | 'not_found' | 'error'
  slug: null,
  carrierName: null,
  carrierLogoUrl: null,
};

// Lazy initializer (not an effect) so a real tenant subdomain starts life
// already in 'loading' status on the very first render -- otherwise
// CarrierAuth would flash the generic signup UI for one frame (initial
// 'none' state) before the effect below flips it, since parsing the
// hostname is synchronous and there's no reason to defer it.
const getInitialState = () => {
  if (typeof window === 'undefined') return INITIAL_STATE;
  const slug = getTenantSlugFromHostname(window.location.hostname);
  return slug ? { status: 'loading', slug, carrierName: null, carrierLogoUrl: null } : INITIAL_STATE;
};

export const TenantBrandingProvider = ({ children }) => {
  const [state, setState] = useState(getInitialState);

  useEffect(() => {
    // Recomputed here (cheap, pure, synchronous) rather than read off
    // `state` so this effect stays self-contained with an empty deps array.
    const slug = getTenantSlugFromHostname(window.location.hostname);
    if (!slug) return; // 'none' -- no network call at all.

    let cancelled = false;

    client.rpc('get_carrier_public_branding', { p_slug: slug })
      .then((rows) => {
        if (cancelled) return;
        const row = Array.isArray(rows) ? rows[0] : rows;
        if (!row) {
          setState({ status: 'not_found', slug, carrierName: null, carrierLogoUrl: null });
          return;
        }
        setState({
          status: 'found',
          slug,
          carrierName: row.name || null,
          carrierLogoUrl: row.logo_url || null,
        });
      })
      .catch((err) => {
        if (cancelled) return;
        // Transient network/RPC failure -- treated as tenant-scoped for UI
        // purposes (hide signup, generic subtitle) rather than "not found",
        // so a blip never tells a real customer their company doesn't exist.
        console.error('[TenantBrandingContext] get_carrier_public_branding failed:', err);
        setState({ status: 'error', slug, carrierName: null, carrierLogoUrl: null });
      });

    return () => { cancelled = true; };
  }, []);

  return (
    <TenantBrandingContext.Provider value={state}>
      {children}
    </TenantBrandingContext.Provider>
  );
};

export const useTenantBranding = () => {
  const context = useContext(TenantBrandingContext);
  if (!context) {
    throw new Error('useTenantBranding must be used within a TenantBrandingProvider');
  }
  return context;
};
